import { useState } from 'react';
import { Box, Loader, Modal, Text, ThemeIcon, Tooltip, UnstyledButton } from '@mantine/core';
import {
    IconPlus,
    IconPlayerPlay,
    IconPlayerStop,
    IconCheck,
    IconSend,
    IconUsers,
    IconUserCheck,
    IconArrowBack,
    IconMedicineSyrup,
    IconPencil,
    IconClock,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useOTPTimeline } from '../../../global/entities/OTP/queries';
import { CoMedicationChecklistModal } from './OTPCoMedicationChecklist/CoMedicationChecklistModal';

interface OTPEventTimelineProps {
    otpId: number | undefined;
}

const ACTION_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
    Created: { icon: IconPlus, color: 'blue' },
    Activated: { icon: IconPlayerPlay, color: 'green' },
    Aborted: { icon: IconPlayerStop, color: 'red' },
    Completed: { icon: IconCheck, color: 'green' },
    SubmittedForValidation: { icon: IconSend, color: 'cyan' },
    DelegatedForValidation: { icon: IconUsers, color: 'violet' },
    ValidatedByDelegatedHcp: { icon: IconUserCheck, color: 'teal' },
    ReturnedToDraft: { icon: IconArrowBack, color: 'orange' },
    CoMedicationChecklistSaved: { icon: IconMedicineSyrup, color: 'yellow' },
    ParametersUpdated: { icon: IconPencil, color: 'gray' },
};

const DEFAULT_CONFIG = { icon: IconClock, color: 'gray' };

const HIDDEN_ACTIONS = ['Saved'];

const CLICKABLE_ACTIONS = ['CoMedicationChecklistSaved'];

export function OTPEventTimeline({ otpId }: OTPEventTimelineProps) {
    const { t, i18n } = useTranslation();
    const { data: events, isLoading } = useOTPTimeline(otpId);
    const [coMedModalOpened, setCoMedModalOpened] = useState(false);
    const [detailsModal, setDetailsModal] = useState<{ title: string; content: string } | null>(null);

    if (isLoading) return <Loader size="sm" mt="sm" />;
    if (!events || events.length === 0) return null;

    const filteredEvents = events.filter((e) => !HIDDEN_ACTIONS.includes(e.action));

    if (filteredEvents.length === 0) return null;

    const formatDate = (dateStr: string) => {
        const date = new Date(dateStr);
        return date.toLocaleString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Box mt="md">
            <Text size="sm" fw={600} mb="sm">
                {t('otp.timeline.title', 'Event History')}
            </Text>
            <Box
                style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    rowGap: 16,
                    padding: '8px 0 16px 0',
                }}
            >
                {filteredEvents.map((event, index) => {
                    const config = ACTION_CONFIG[event.action] || DEFAULT_CONFIG;
                    const Icon = config.icon;
                    const isFirst = index === 0;
                    const isLast = index === filteredEvents.length - 1;
                    return (
                        <Box
                            key={event.id}
                            style={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: 140,
                            }}
                        >
                            {/* Left connector: item left edge → icon left edge */}
                            {!isFirst && (
                                <Box
                                    style={{
                                        position: 'absolute',
                                        top: 13,
                                        left: 0,
                                        width: 'calc(50% - 14px)',
                                        height: 2,
                                        backgroundColor: 'var(--mantine-color-gray-3)',
                                    }}
                                />
                            )}
                            {/* Right connector: icon right edge → item right edge */}
                            {!isLast && (
                                <Box
                                    style={{
                                        position: 'absolute',
                                        top: 13,
                                        right: 0,
                                        width: 'calc(50% - 14px)',
                                        height: 2,
                                        backgroundColor: 'var(--mantine-color-gray-3)',
                                    }}
                                />
                            )}
                            {(() => {
                                const hasActivationNote =
                                    event.action === 'Activated' &&
                                    !!event.details?.PatientAcknowledgmentNote;
                                const hasOutcomeNote =
                                    (event.action === 'Completed' || event.action === 'Aborted' || event.action === 'Appended') &&
                                    !!(event.details?.ReasonCode || event.details?.ReasonText);
                                const isClickable =
                                    CLICKABLE_ACTIONS.includes(event.action) ||
                                    hasActivationNote ||
                                    hasOutcomeNote;
                                const handleClick = () => {
                                    if (event.action === 'CoMedicationChecklistSaved') 
                                        setCoMedModalOpened(true);
                                     else if (hasActivationNote) {
                                        setDetailsModal({
                                            title: t('otp.timeline.details.PatientAcknowledgmentNote', 'Patient note'),
                                            content: event.details!.PatientAcknowledgmentNote,
                                        });
                                    } else if (hasOutcomeNote) {
                                        const parts: string[] = [];
                                        if (event.details?.ReasonCode)
                                            parts.push(`${t('otp.timeline.details.ReasonCode', 'Reason')}: ${event.details.ReasonCode}`);
                                        if (event.details?.ReasonText)
                                            parts.push(event.details.ReasonText);
                                        setDetailsModal({
                                            title: t(`otp.timeline.actions.${event.action}`, event.action),
                                            content: parts.join('\n'),
                                        });
                                    }
                                };
                                const icon = (
                                    <Tooltip
                                        label={
                                            <>
                                                {t(`otp.timeline.actions.${event.action}`, event.action)}
                                                {event.actorName && (
                                                    <> — {event.actorName}</>
                                                )}
                                            </>
                                        }
                                        withArrow
                                        multiline
                                        w={200}
                                    >
                                        <ThemeIcon
                                            size={28}
                                            variant="filled"
                                            color={config.color}
                                            radius="xl"
                                            style={{ zIndex: 1, cursor: isClickable ? 'pointer' : undefined }}
                                        >
                                            <Icon size={14} />
                                        </ThemeIcon>
                                    </Tooltip>
                                );
                                return (
                                    <>
                                        {isClickable ? (
                                            <UnstyledButton onClick={handleClick}>{icon}</UnstyledButton>
                                        ) : (
                                            icon
                                        )}
                                        <Text
                                            size="xs"
                                            fw={500}
                                            ta="center"
                                            mt={6}
                                            lh={1.2}
                                            td={isClickable ? 'underline' : undefined}
                                            c={isClickable ? 'blue' : undefined}
                                            style={{ cursor: isClickable ? 'pointer' : undefined }}
                                            onClick={isClickable ? handleClick : undefined}
                                        >
                                            {t(`otp.timeline.actions.${event.action}`, event.action)}
                                        </Text>
                                    </>
                                );
                            })()}
                            <Text size="xs" c="dimmed" ta="center" mt={2} lh={1.2}>
                                {formatDate(event.eventDate)}
                            </Text>
                            {event.actorName && (
                                <Text size="xs" c="dimmed" ta="center" lh={1.2}>
                                    {event.actorName}
                                </Text>
                            )}
                        </Box>
                    );
                })}
            </Box>

            {otpId && (
                <CoMedicationChecklistModal
                    opened={coMedModalOpened}
                    onClose={() => setCoMedModalOpened(false)}
                    otpId={otpId}
                    onSubmitSuccess={() => setCoMedModalOpened(false)}
                    readOnly
                />
            )}

            <Modal
                opened={!!detailsModal}
                onClose={() => setDetailsModal(null)}
                title={detailsModal?.title}
                size="md"
            >
                <Text size="sm" style={{ whiteSpace: 'pre-line' }}>{detailsModal?.content}</Text>
            </Modal>
        </Box>
    );
}
